// Voodoo Doll Card Play Animation Effect
// Sprite sheet animation that slides from left side of screen to center with deceleration

// Configuration
const CONFIG = {
    // Sprite sheet config (8x8 grid, 60 frames, last 4 empty)
    sheetCols: 8,
    sheetRows: 8,
    totalFrames: 60,
    fps: 10,

    // Motion config
    slideStopFrame: 30, // Frame at which sliding stops

    // Paths  
    sheetPath: '/assets/effects/voodoo_doll/voodoo_doll_anim.png'
}

// Image cache
const spriteCache: Record<string, HTMLImageElement> = {}

const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image()
        img.src = src
        spriteCache[src] = img
    }
    return spriteCache[src]
}

// Preload texture
const preloadTexture = () => {
    getSprite(CONFIG.sheetPath)
}
preloadTexture()

export type VoodooDollData = {
    currentFrame: number
    posX: number
    posY: number
    startX: number
    targetX: number
    frameWidth: number
    frameHeight: number
    initialized: boolean
}

export type VoodooDollEffect = {
    kind: 'voodooDoll'
    t: number
    isOpponent: boolean
    data?: VoodooDollData
}

export const createVoodooDollEffect = (isOpponent: boolean = false): VoodooDollEffect => {
    return {
        kind: 'voodooDoll',
        t: 0,
        isOpponent
    }
}

// Ease out cubic for smooth deceleration
const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3)
}

export const renderVoodooDollEffect = (
    eff: VoodooDollEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    const img = getSprite(CONFIG.sheetPath)

    // Wait for image to load
    if (!img.complete || img.naturalWidth === 0) {
        return true
    }

    // Initialize data if not yet done
    if (!eff.data) {
        // Calculate frame dimensions from loaded image
        // Use Math.floor to prevent sub-pixel drift which causes "walking" inside the frame
        const frameWidth = Math.floor(img.naturalWidth / CONFIG.sheetCols)
        const frameHeight = Math.floor(img.naturalHeight / CONFIG.sheetRows)

        const screenCenterX = window.innerWidth / 2
        const screenCenterY = window.innerHeight / 2

        // Start off-screen left
        const startX = -frameWidth
        const targetX = screenCenterX

        // Shift up by 50px
        const yOffset = -50

        eff.data = {
            currentFrame: 0,
            posX: startX,
            posY: screenCenterY + yOffset,
            startX,
            targetX,
            frameWidth,
            frameHeight,
            initialized: true
        }
    }

    const d = eff.data
    const msPerFrame = 1000 / CONFIG.fps

    // Calculate current frame
    d.currentFrame = Math.floor(eff.t / msPerFrame)

    // Check if animation is complete
    if (d.currentFrame >= CONFIG.totalFrames) {
        return false // Effect complete
    }

    // Calculate position based on current frame
    if (d.currentFrame < CONFIG.slideStopFrame) {
        // Sliding phase: ease-out from start to center
        const slideProgress = d.currentFrame / CONFIG.slideStopFrame
        const easedProgress = easeOutCubic(slideProgress)
        d.posX = d.startX + (d.targetX - d.startX) * easedProgress
    } else {
        // Stopped at center
        d.posX = d.targetX
    }

    // Calculate sprite sheet coordinates
    const leadingFrames = 0 // If there are any leading empty frames, adjust here. Assuming 0 based on description.
    const frameIndex = (d.currentFrame + leadingFrames) % CONFIG.totalFrames
    const col = frameIndex % CONFIG.sheetCols
    const row = Math.floor(frameIndex / CONFIG.sheetCols)

    const sx = Math.floor(col * d.frameWidth)
    const sy = Math.floor(row * d.frameHeight)

    const renderSprite = (x: number, y: number, isShadow: boolean) => {
        ctx.save()

        ctx.translate(x, y)

        if (eff.isOpponent) {
            ctx.scale(-1, 1)
        }

        if (isShadow) {
            // Shadow settings: simple drop shadow effect
            ctx.globalAlpha = 0.3
            ctx.filter = 'brightness(0)'
        }

        ctx.drawImage(
            img,
            sx, sy, d.frameWidth, d.frameHeight,
            -d.frameWidth / 2, -d.frameHeight / 2, d.frameWidth, d.frameHeight
        )

        ctx.restore()
    }

    // Draw Shadow First
    // Offset shadow slightly to simulate depth/ground distance
    const shadowOffsetX = 10
    const shadowOffsetY = 50 // Place shadow "on ground" relative to floating doll

    // Adjust shadow position based on sprite flip if needed? 
    // If we flip context inside renderSprite, simpler to pass coords.
    // BUT if we flip, shadow offset X might need inversion if we want absolute light source.
    // Let's keep it simple: Shadow attached to object.

    // We render shadow using the same transform logic inside renderSprite, 
    // so we just pass the center coordinates.
    // Actually, distinct shadow position is better.

    renderSprite(d.posX + shadowOffsetX, d.posY + shadowOffsetY, true)

    // Draw Main Sprite
    renderSprite(d.posX, d.posY, false)

    return true
}
